import { ApplicationAnswers, FieldType } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import InfoDisplay from '@platform-ui-common/components/InfoDisplay/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useApplication from '@platform-ui-common/hooks/contexts/useApplication.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useProgram from '@platform-ui-common/hooks/contexts/useProgram.js';
import useForm from '@platform-ui-common/hooks/useForm/index.js';
import { ValidationMode } from '@platform-ui-common/hooks/useForm/index.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import useTaxForms from '@platform-ui-common/hooks/useTaxForms.js';
import Color, { BackgroundColors } from '@platform-ui-common/utilities/Color.js';
import { useValidateAddresses } from '@platform-ui-common/utilities/address.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import {
  useSubmitApplication,
  useUpdateApplication,
} from '@platform-ui/application/hooks/index.js';
import useApplicationConfig from '@platform-ui/application/hooks/useApplicationConfig.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { getNextPage, getQuestion } from '@platform-ui/utilities/application/config.js';
import {
  FieldContext,
  extractFieldValidation,
  isFieldEnabled,
} from '@platform-ui/utilities/application/fields/index.js';
import {
  getQuestionFields,
  getQuestionFormInit,
  getQuestionUpdate,
  isPrefilled,
} from '@platform-ui/utilities/application/question.js';
import cloneDeep from 'lodash/cloneDeep.js';
import React, { ReactNode, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import FullWidthPanel from '../../components/FullWidthPanel/index.js';
import PrefilledChip from '../../components/PrefilledChip/index.js';
import VerifiedPortal from '../../components/VerifiedPortal/index.js';
import Field from './fields/Field.js';

export const setSkippedByPath = (
  answers: Partial<ApplicationAnswers>,
  path: string,
): Partial<ApplicationAnswers> => {
  const clone = cloneDeep(answers);
  const props = path.split('.');
  const setSkipped = (
    index: number,
    answers: Partial<ApplicationAnswers> = {},
  ): Partial<ApplicationAnswers> => {
    const prop = props[index];
    if (index === props.length - 1) {
      answers[prop] = { skipped: true };
      return answers;
    }
    answers[prop] = setSkipped(index + 1, answers[prop]);
    return answers;
  };
  return setSkipped(0, clone);
};

function PanelWrapper({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="w-full lg:w-1/2 lg:min-w-[45rem]">
      <FullWidthPanel>
        <div
          className={`${
            BackgroundColors[Color.LightBackground]
          } w-full flex flex-col gap-x-4 gap-y-5 px-5 lg:px-8 py-6`}
        >
          {children}
        </div>
      </FullWidthPanel>
    </div>
  );
}

// Without this wrapper, since the route is the same for every question, when
// moving from one question page to another, it renders the same instance of the
// component; just the return value of useParams changes. This messes with the
// behavior of the useForm hook, so we use this wrapper to force a new instance
// of the component to be rendered when the question changes.
export default function ApplicationQuestionWrapper(): JSX.Element {
  const { questionKey: questionKeyRaw } = useParams<{ questionKey: string }>();
  const questionKey = questionKeyRaw.replace(/-/g, '.');
  return <ApplicationQuestion key={questionKey} questionKey={questionKey} />;
}

function ApplicationQuestion({ questionKey }: { questionKey: string }): JSX.Element {
  const application = useApplication();
  const { answers } = application;
  const config = useApplicationConfig();
  const submitApplication = useSubmitApplication();
  const updateApplication = useUpdateApplication();
  const { taxForms } = useTaxForms();

  const { user } = useAuth();
  const program = useProgram();
  const partner = usePartner();
  const question = getQuestion(config, questionKey);
  const placeholders = usePlaceholder();

  const [initialData, validationConfig] = useMemo(
    () => getQuestionFormInit(question, answers, application),
    [question, answers, application],
  );
  const {
    formData,
    dispatch,
    errors,
    counters: { missingRequiredFields, requiredFields },
    trySubmit,
  } = useForm(initialData, validationConfig, ValidationMode.RequiredOnSubmit);
  const { loading: geocoderLoading, validateAddresses } = useValidateAddresses();
  const [validationOverrides, setValidationOverrides] = useState<Record<string, boolean>>({});
  const hasAddressField = question.fields.some((field) => field.type === FieldType.Address);
  const [showManualAddressBtn, setShowManualAddressBtn] = useState(false);
  const [manualAddressEntryMode, setManualAddressEntryMode] = useState(false);

  const onSkip = (): void => {
    const update = setSkippedByPath(answers, questionKey);
    updateApplication.doMutation({
      answers: update,
      addresses: [],
      documents: [],
      next: getNextPage(config, question, update, program, taxForms),
    });
  };

  const isSubmissionQuestion = !getNextPage(config, question, answers, program, taxForms);
  const validationOverrideCounts = Object.values(validationOverrides).filter(Boolean).length;
  const onSubmit = (): void =>
    trySubmit(async (): Promise<void> => {
      if (validationOverrideCounts > 0) return;
      const { addresses, ...update } = getQuestionUpdate(question, answers, {
        ...formData,
        ...(question.skippable && { [`${questionKey}.skipped`]: false }),
      });

      const validatedAddresses = await validateAddresses(addresses, {
        manualOverride: manualAddressEntryMode,
      });
      const next = getNextPage(
        config,
        question,
        { ...answers, ...update.answers },
        program,
        taxForms,
      );
      const success = await updateApplication.doMutation({
        ...update,
        addresses: validatedAddresses,
        next,
      });
      if (!next && success) await submitApplication.doMutation();
    });

  const loading = [updateApplication.loading, submitApplication.loading, geocoderLoading].find(
    Boolean,
  );

  const prefilledAnswer = isPrefilled(
    question.fields,
    user?.core?.applicantProfile,
    partner?.applicantProfileConfigs,
    answers,
  );

  const FieldsWrapper = question.layout === 'panel' ? PanelWrapper : React.Fragment;

  // If the question has an address field, we need to adjust the number of missing required fields
  // to account for the visibility of the manual address entry fields.  This can be extended in the
  // future to hold additional logic for other field types.
  const computeMissingRequiredFields = () =>
    hasAddressField && !manualAddressEntryMode ? missingRequiredFields - 3 : missingRequiredFields;

  const showManualAddressMessaging =
    hasAddressField && showManualAddressBtn && !manualAddressEntryMode;

  return (
    <FormContainer
      title={question.copy.title}
      onSubmit={onSubmit}
      chip={prefilledAnswer && <PrefilledChip />}
      buttons={
        <div className="flex flex-col gap-4 items-center">
          <InfoDisplay
            message="Can't find your address? Click the button below to manually input your address."
            visible={showManualAddressMessaging}
            classNames="w-field mb-6"
          />
          <ErrorDisplay
            errors={computeMissingRequiredFields() + validationOverrideCounts}
            visible={missingRequiredFields > 0 || validationOverrideCounts > 0}
          />
          <Button type="submit" loading={loading}>
            {isSubmissionQuestion ? 'Submit Application' : 'Save And Continue'}
          </Button>
          {question.skippable && (
            <Button variant="outline" onClick={onSkip} loading={loading}>
              Skip
            </Button>
          )}
          {showManualAddressMessaging && (
            <Button
              variant="outline"
              onClick={(): void => {
                setManualAddressEntryMode(true);
                setShowManualAddressBtn(false);
              }}
            >
              Manually input address
            </Button>
          )}
        </div>
      }
    >
      {question.copy.intro && (
        <div
          id="question-intro"
          className="w-full max-w-[36rem] mt-2 mb-4 flex flex-col items-center gap-4"
        >
          <Markdown variant="largeBody" alignment="center">
            {replacePlaceholders(question.copy.intro, placeholders)}
          </Markdown>
        </div>
      )}
      {!!requiredFields && (
        <Typography key="required-helper" variant="body" tag="span" alignment="center">
          (*) An asterisk indicates a required response
        </Typography>
      )}
      <FieldsWrapper>
        {question.fields
          .filter((field) => isFieldEnabled(field, formData))
          .map(
            (field): JSX.Element => (
              <Field
                key={field.key}
                context={question.layout === 'panel' ? FieldContext.Panel : FieldContext.Base}
                answers={formData}
                dispatch={dispatch}
                setValidationOverrides={setValidationOverrides}
                field={field}
                question={question}
                validation={extractFieldValidation(field, validationConfig)}
                errors={errors}
                showManualAddressButton={setShowManualAddressBtn}
                showManualInput={manualAddressEntryMode}
              />
            ),
          )}
      </FieldsWrapper>
      {getQuestionFields(question.fields).some(({ type }) => type === FieldType.Document) && (
        <VerifiedPortal>
          These documents will only be viewed by {partner.name} to verify your eligibility. This is
          a safe and secure portal.
        </VerifiedPortal>
      )}
    </FormContainer>
  );
}
